import { User, useUsers } from '@/lib/auth-service';
import SettingsTabTitle from '../settings-tab-title';
import { UserSettingsRow } from '@/components/compound-components/settings/users-tab/user-settings-row';
import { useOrganizationContext } from '@/contexts/organization-context';
import { Spinner } from '@/components/basic/spinner';
import { CommunicationChannelTypes } from '@/__generated__/graphql';
import { gql } from '@/__generated__';
import { useMutation } from '@apollo/client';
import { Button } from '@/components/shadcn/button';
import { PlusIcon } from '@radix-ui/react-icons';
import { useState } from 'react';

const UpdateContactPointsMutation = gql(`
  mutation UpdateContactPointsMutation($input: OrganizationUpdateInput!) {
    organization {
      update(input: $input) {
        ... on OrganizationUpdateSuccess {
          result {
            id
            contactPoints {
              communicationChannels {
                addressee
                type
              }
              updates {
                socialMedia
                grants
              }
            }
          }
        }
      }
    }
  }
`);

export function UsersTab() {
  const { organizationId } = useOrganizationContext();
  const { users, contactPoints, isLoading, refreshUsers } = useUsers(
    organizationId as string
  );
  const [addingUser, setAddingUser] = useState<boolean>(false);

  const [updateContactPointsMutation] = useMutation(
    UpdateContactPointsMutation,
    {
      onCompleted: () => {
        refreshUsers();
      },
    }
  );

  const updateContactPoints = async (user: User, isDeleting = false) => {
    if (!contactPoints || !organizationId) {
      return;
    }

    let updatedContactPoints;

    if (isDeleting) {
      updatedContactPoints = contactPoints.filter(
        (cp) =>
          !cp?.communicationChannels.some(
            (channel) => channel.addressee === user.email
          )
      );
    } else {
      updatedContactPoints = contactPoints.map((cp) => {
        if (
          cp?.communicationChannels.some(
            (channel) => channel.addressee === user.email
          )
        ) {
          return {
            ...cp,
            updates: user.updates,
          };
        }
        return cp;
      });

      if (
        !updatedContactPoints.some((cp) =>
          cp?.communicationChannels.some(
            (channel) => channel.addressee === user.email
          )
        )
      ) {
        updatedContactPoints.push({
          communicationChannels: [
            {
              addressee: user.email,
              type: CommunicationChannelTypes.Email,
            },
          ],
          updates: user.updates,
        });
      }
    }

    await updateContactPointsMutation({
      variables: {
        input: {
          filters: {
            id: organizationId,
          },
          set: {
            contactPoints: updatedContactPoints.map((cp) => ({
              communicationChannels: cp.communicationChannels.map((cc) => ({
                addressee: cc.addressee,
                type: cc.type,
              })),
              updates: {
                grants: cp.updates.grants || false,
                socialMedia: cp.updates.socialMedia || false,
              },
            })),
          },
        },
      },
      refetchQueries: ['OrganizationGetContactPoints'],
    });
    await refreshUsers();
  };

  if (!organizationId) {
    return (
      <div>
        <SettingsTabTitle title='Users and Permissions' />
        <p>Please select an organization first.</p>
      </div>
    );
  }

  return (
    <div>
      <SettingsTabTitle title='Users and Permissions' />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='grid gap-1'>
          <div className='grid grid-cols-20 items-center gap-2'>
            <div className='hidden md:block'></div>
            <div className='col-span-6'>Email</div>
            <div className='col-span-6'>Role</div>
            <div className='col-span-6'>Notifications</div>
            <div className='col-span-1 md:col-span-2'></div>
          </div>

          {!isLoading &&
            users.map((user: User) => (
              <UserSettingsRow
                user={user}
                updateContactPoints={updateContactPoints}
                refreshUsers={refreshUsers}
                key={user.id || user.email}
              />
            ))}
          {addingUser && (
            <UserSettingsRow
              updateContactPoints={updateContactPoints}
              refreshUsers={refreshUsers}
              setAddingUser={setAddingUser}
              user={{
                email: '',
                updates: { grants: false, socialMedia: false },
              }}
            />
          )}
          <Button
            variant='ghost'
            size='sm'
            className='mt-1 h-fit place-self-start px-1 py-1 pr-2 hover:bg-gray-settings-button'
            onClick={(e) => {
              e.stopPropagation();
              setAddingUser(true);
            }}
          >
            <div className='flex space-x-2'>
              <PlusIcon />
              <span>Add a new user</span>
            </div>
          </Button>
        </div>
      )}
    </div>
  );
}
